import React, {useState} from 'react';
import {Checkbox, FormControlLabel, Paper, Typography, useMediaQuery} from "@mui/material";
import {Container} from "@mui/system";
import Box from "@mui/material/Box";
import ButtonText from "../../components/UI/button/ButtonText";
import {SearchComponent} from "../../components/UI/search/Search";
import {Link, useNavigate} from "react-router-dom";
import {ReactComponent as ViberIcon} from "../../components/Icons/viber.svg"
import {ReactComponent as YoutubeIcon} from "../../components/Icons/youtube.svg"
import {ReactComponent as TelegramIcon} from "../../components/Icons/telegram.svg"
import {ReactComponent as Pay1Icon} from "../../components/Icons/pay1.svg"
import {ReactComponent as Pay2Icon} from "../../components/Icons/pay2.svg"
import {ReactComponent as Pay3Icon} from "../../components/Icons/pay3.svg"
import {ReactComponent as Pay4Icon} from "../../components/Icons/pay4.svg"
import {constantsErrorMessages} from "../../Constants";
import UtilityService from "../../API/common/UtilityService";
import ModalAlert from "../../components/UI/table/customer-detales/ModalAlert";
import {versoMebelContacts} from "../../data/ContactsVersoMebel";
import {isKuhniVersoMebelDomain, kuhniVersoTabletContainerQuerySx} from "../../utils/isKuhniVersoMebelDomain";
import VersoMebelLogo from "../../components/UI/VersoMebelLogo";

const MEDIA_MAX_1440 = "@media (max-width: 1440px)";
const MEDIA_COMPACT = "@media (max-width: 1440px) and (min-width: 641px)";
const MEDIA_MAX_800 = "@media (max-width: 800px)";
const MEDIA_MAX_1023 = "@media (max-width: 1023px)";
const MEDIA_MAX_640 = "@media (max-width: 640px)";
const MEDIA_MAX_480 = "@media (max-width: 480px)";
const footerSecondRowTextSx = {
    fontSize: "14px",
    lineHeight: "18px",
    color: (theme) => theme.palette.text.main,
};

const footerCreditsLineSx = {
    fontSize: "13px",
    lineHeight: "16px",
    color: (theme) => theme.palette.text.light,
    "& a": {
        color: (theme) => theme.palette.text.light,
        textDecoration: "none",
    },
};

const footerGridSx = {
    display: "grid",
    gridTemplateColumns: "repeat(5, auto)",
    justifyContent: "space-between",
    justifyItems: "stretch",
    columnGap: "40px",
    rowGap: "16px",
    width: "100%",
    alignItems: "start",
    [MEDIA_COMPACT]: {
        columnGap: "100px",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, max-content)",
        minWidth: 0,
        rowGap: "24px",
        gridTemplateAreas: `
            "logo subscribe subscribe"
            "dividerTop dividerTop dividerTop"
            "menu payment contacts"
            "divider divider divider"
            "policy policy credits"
            "copyright copyright copyright"
        `,
        justifyContent: "stretch",
    },
    [MEDIA_MAX_1023]: {
        columnGap: "40px",
    },
    [MEDIA_MAX_800]: {
        columnGap: "16px",
    },
    [MEDIA_MAX_640]: {
        gridTemplateColumns: "minmax(0, max-content) minmax(0, max-content)",
        minWidth: 0,
        justifyContent: "space-between",
        justifyItems: "start",
        gridTemplateAreas: `
            "logo logo"
            "subscribe subscribe"
            "dividerTop dividerTop"
            "menu menu"
            "dividerMenu dividerMenu"
            "payment contacts"
            "divider divider"
            "policy credits"
            "copyright copyright"
        `,
    },
    [MEDIA_MAX_480]: {
        gridTemplateColumns: "1fr",
        columnGap: 0,
        gridTemplateAreas: `
            "logo"
            "subscribe"
            "dividerTop"
            "menu"
            "dividerMenu"
            "payment"
            "contacts"
            "divider"
            "policy"
            "credits"
            "copyright"
        `,
    },
};

const footerLogoSx = {
    [MEDIA_COMPACT]: {
        gridArea: "logo",
        width: "100%",
        minWidth: 0,
    },
    [MEDIA_MAX_640]: {
        gridArea: "logo",
        gridColumn: "1 / -1",
    },
};

const footerSubscribeWrapSx = {
    [MEDIA_COMPACT]: {
        gridArea: "subscribe",
        gridColumn: "2 / 4",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
    },
    [MEDIA_MAX_640]: {
        gridArea: "subscribe",
        gridColumn: "1 / -1",
    },
};

const footerSubscribeSx = {
    display: "flex",
    flexDirection: "column",
    gridGap: "16px",
    width: 358,
    [MEDIA_MAX_1440]: {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
    },
};

const footerSubscribeTitleSx = {
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "24px",
    [MEDIA_MAX_1440]: {
        minWidth: 0,
        overflowWrap: "anywhere",
    },
};

const footerSubscribeConsentSx = {
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
    marginLeft: 0,
    marginRight: 0,
    minWidth: 0,
    width: "100%",
    "& .MuiFormControlLabel-label": {
        marginLeft: 0,
        minWidth: 0,
    },
    "& .MuiTypography-root": {
        color: (theme) => theme.palette.text.light,
        fontSize: "12px",
        lineHeight: "14px",
        minWidth: 0,
        overflowWrap: "anywhere",
    },
    "& a": {
        color: (theme) => theme.palette.primary.main,
        textDecoration: "none",
    },
};

const footerMenuSx = {
    width: "max-content",
    minWidth: 136,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gridGap: "16px",
    flexShrink: 0,
    textAlign: "left",
    "& .MuiButton-root": {
        fontSize: "16px",
        lineHeight: "20px",
    },
    [MEDIA_COMPACT]: {
        gridArea: "menu",
        width: "100%",
        minWidth: 0,
    },
    [MEDIA_MAX_640]: {
        gridArea: "menu",
        gridColumn: "1 / -1",
    },
};

const footerPaymentIconsSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
    maxWidth: "250px",
    [MEDIA_MAX_480]: {
        maxWidth: "none",
        alignItems: "stretch",
    },
};

const footerPaymentSx = {
    width: 230,
    display: "flex",
    flexDirection: "column",
    gridGap: "16px",
    [MEDIA_COMPACT]: {
        gridArea: "payment",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "start",
    },
    [MEDIA_MAX_640]: {
        gridArea: "payment",
        gridColumn: "auto",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "start",
        "& .MuiTypography-root": {
            overflowWrap: "anywhere",
        },
    },
    [MEDIA_MAX_480]: {
        gridColumn: "1 / -1",
    },
};

const footerContactsSx = {
    width: 211,
    display: "flex",
    flexDirection: "column",
    gridGap: "16px",
    textAlign: "left",
    [MEDIA_COMPACT]: {
        gridArea: "contacts",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "start",
    },
    [MEDIA_MAX_640]: {
        gridArea: "contacts",
        gridColumn: "auto",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "end",
    },
    [MEDIA_MAX_480]: {
        gridColumn: "1 / -1",
        justifySelf: "start",
        alignItems: "flex-start",
    },
};

const footerDividerLineSx = {
    gridColumn: "1 / -1",
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    justifySelf: "stretch",
    boxSizing: "border-box",
    border: "none",
    borderTop: "1px solid #EAEBEA",
    margin: 0,
    padding: 0,
};

const footerDividerTopSx = {
    ...footerDividerLineSx,
    display: "none",
    [MEDIA_MAX_1440]: {
        display: "block",
    },
    [MEDIA_COMPACT]: {
        gridArea: "dividerTop",
        gridColumn: "1 / -1",
    },
    [MEDIA_MAX_640]: {
        gridArea: "dividerTop",
        gridColumn: "1 / -1",
    },
};

const footerDividerMenuSx = {
    ...footerDividerLineSx,
    display: "none",
    [MEDIA_MAX_640]: {
        display: "block",
        gridArea: "dividerMenu",
        gridColumn: "1 / -1",
    },
    [MEDIA_MAX_480]: {
        gridColumn: "1 / -1",
    },
};

const footerDividerSx = {
    ...footerDividerLineSx,
    gridArea: "auto",
    [MEDIA_COMPACT]: {
        gridArea: "divider",
        gridColumn: "1 / -1",
    },
    [MEDIA_MAX_640]: {
        gridArea: "divider",
        gridColumn: "1 / -1",
    },
};

const footerBottomCopyrightSx = {
    gridColumn: "1 / 4",
    ...footerSecondRowTextSx,
    [MEDIA_COMPACT]: {
        gridArea: "copyright",
        gridColumn: "1 / -1",
        width: "100%",
        whiteSpace: "normal",
    },
    [MEDIA_MAX_640]: {
        gridArea: "copyright",
        gridColumn: "1 / -1",
    },
};

const footerBottomPolicySx = {
    gridColumn: "4",
    alignSelf: "center",
    ...footerSecondRowTextSx,
    [MEDIA_COMPACT]: {
        gridArea: "policy",
        gridColumn: "1 / 3",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        alignSelf: "start",
        justifySelf: "stretch",
        textAlign: "left",
    },
    [MEDIA_MAX_640]: {
        gridArea: "policy",
        gridColumn: "auto",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "start",
        overflowWrap: "anywhere",
    },
    [MEDIA_MAX_480]: {
        gridColumn: "1 / -1",
    },
};

const footerBottomCreditsSx = {
    gridColumn: "5",
    width: "210px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    justifySelf: "end",
    [MEDIA_COMPACT]: {
        gridArea: "credits",
        gridColumn: "3 / 4",
        justifySelf: "start",
        width: "max-content",
        maxWidth: "100%",
        alignItems: "flex-start",
        textAlign: "left",
    },
    [MEDIA_MAX_640]: {
        gridArea: "credits",
        gridColumn: "auto",
        width: "max-content",
        minWidth: 0,
        maxWidth: "100%",
        justifySelf: "end",
        flexDirection: "column",
        alignItems: "flex-start",
        "& .MuiTypography-root": {
            overflowWrap: "anywhere",
        },
    },
    [MEDIA_MAX_480]: {
        gridColumn: "1 / -1",
        width: "100%",
        minWidth: 0,
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "16px",
        justifySelf: "stretch",
        "& > .MuiTypography-root": {
            flex: "1 1 0",
            minWidth: 0,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
        },
    },
};

const Footer = () => {
    const domainName = window.location.hostname;
    const isKuhniVersoMebel = isKuhniVersoMebelDomain(domainName)
    const [privacyPolicy, setPrivacyPolicy] = useState(false)
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const [alertData, setAlertData] = useState({});
    const navigate = useNavigate();
    const isFooterCompact = useMediaQuery("(max-width:1440px)");

    const handleModalAlert = (message, type = "error") => {
        setAlertData({
            type: type,
            open: true,
            message: type === "success" ? message : message.response?.data?.message || constantsErrorMessages[message.response?.status] || message.message
        })
    }
    const handleSuccess = () => {
        setAlertData({
            type: 'success',
            open: true,
            message: "Вы подписались на рассылку"
        })
    }
    const handleInputChange = (event) => {
        setInputValue(event.target.value)
        setError("")
    }

    const handleSubscribe = () => {
        if (inputValue === "") {
            setError("Введите почту")
        } else if (!privacyPolicy) {
            setError("Подтвердите политику конфединциальности")
        } else {
            UtilityService.userMailing(inputValue).then(function (response) {
                handleSuccess()
                setInputValue("")
                setPrivacyPolicy(false)
                handleModalAlert("Подписка добавлена", 'success')
            })
                .catch(function (error) {
                    handleModalAlert(error);
                });
        }
    }
    return (
        <Paper sx={{
            width: '100%',
            padding: "40px 0",
            display: 'flex',
            flexShrink: 0,
            justifyContent: "space-between",
            backgroundColor: (theme) => theme.palette.primary.lightGrey,
        }} component="footer">
            <Container sx={{
                "&.MuiContainer-root": {
                    maxWidth: "1668px",
                    ...(isKuhniVersoMebel ? kuhniVersoTabletContainerQuerySx : {}),
                },
            }}>
                <Box sx={footerGridSx}>
                    <Box sx={footerLogoSx}>
                        {isKuhniVersoMebel ? (
                            <VersoMebelLogo height={56} textSizeScale={0.45} fontWeight={800} />
                        ) : (
                            <Typography
                                variant="h6"
                                noWrap
                                component="a"
                                href="/"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "32px",
                                    letterSpacing: ".3rem",
                                    color: (theme) => theme.palette.primary.main,
                                    textDecoration: "none",
                                }}
                            >
                                UpMe
                            </Typography>
                        )}
                    </Box>
                    <Box sx={footerSubscribeWrapSx}>
                    <Box sx={footerSubscribeSx}>
                        <Typography component="p" sx={footerSubscribeTitleSx}>
                            Подпишитесь на рассылку и будьте в курсе!
                        </Typography>
                        <Box sx={{width: "100%", minWidth: 0}}>
                            <Box sx={{position: "relative", width: "100%", minWidth: 0}}>
                                <SearchComponent
                                    buttonText={"Подписаться"}
                                    onClick={handleSubscribe}
                                    inputValue={inputValue}
                                    handleInputChange={handleInputChange}
                                    fullWidth={isFooterCompact}
                                />
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: "100%",
                                        mt: "4px",
                                        fontSize: "16px",
                                        lineHeight: "20px",
                                        color: (theme) => theme.palette.error.light,
                                    }}
                                >
                                    {error}
                                </Typography>
                            </Box>
                            <FormControlLabel sx={footerSubscribeConsentSx} control={<Checkbox
                                checked={privacyPolicy}
                                onChange={() => {
                                    setPrivacyPolicy(!privacyPolicy)
                                    setError("")
                                }}
                                sx={{
                                    padding: "2px",
                                    "& .MuiSvgIcon-root": {
                                        width: "22px",
                                        height: "22px",
                                        fontSize: "22px",
                                    },
                                }}
                            />} label={<Typography sx={{lineHeight: "14px"}}>
                                Я соглашаюсь с <Link>
                                Политикой конфиденциальности и Обработкой персональных
                                данных</Link>
                            </Typography>}/>
                        </Box>

                    </Box>
                    </Box>
                    <Box role="separator" sx={footerDividerTopSx}/>
                    <Box sx={footerMenuSx}>
                        <ButtonText onClick={() => navigate('/help')} text={"Помощь"}></ButtonText>
                        {!isKuhniVersoMebel && <ButtonText onClick={() => navigate('/news')} text={"Новости"}></ButtonText>}
                        <ButtonText onClick={() => navigate('/contacts')} text={"Контакты"}></ButtonText>
                        {!isKuhniVersoMebel && (
                            <ButtonText onClick={() => navigate('/work')} text={"Работа в UpMe"}></ButtonText>
                        )}
                        <Box
                            component="a"
                            href={process.env.REACT_APP_AUTHORIZATION_BASE_URL}
                            sx={{flexShrink: 0, textDecoration: "none", color: "inherit"}}
                        >
                            <ButtonText text={"Стать партнёром"}></ButtonText>
                        </Box>
                    </Box>
                    <Box role="separator" sx={footerDividerMenuSx}/>
                    <Box sx={footerPaymentSx}>
                        <Typography
                            component="p"
                            sx={{fontSize: "18px", fontWeight: 700, lineHeight: "22px"}}
                        >
                            Мы принимаем к оплате:
                        </Typography>
                        <Box sx={footerPaymentIconsSx}>
                            <Pay1Icon/>
                            <Pay2Icon/>
                            <Pay3Icon/>
                            <Pay4Icon/>
                        </Box>
                    </Box>
                    <Box sx={footerContactsSx}>
                        <Typography
                            component="p"
                            sx={{fontSize: "18px", fontWeight: 500, lineHeight: "22px"}}
                        >
                            Наши контакты
                        </Typography>
                        {isKuhniVersoMebel ? (
                            <>
                                <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
                                    {versoMebelContacts.phones.map((phone) => (
                                        <Typography
                                            key={phone.id}
                                            component="p"
                                            sx={{
                                                fontSize: "16px",
                                                lineHeight: "20px",
                                                fontWeight: 500,
                                                "& a": {
                                                    color: (theme) => theme.palette.text.main,
                                                    textDecoration: "none",
                                                },
                                            }}
                                        >
                                            <a href={phone.href}>{phone.display}</a>
                                        </Typography>
                                    ))}
                                </Box>
                                <Typography component="p" sx={{
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                    "& a": {
                                        color: (theme) => theme.palette.primary.main,
                                        textDecoration: "none",
                                    },
                                }}>
                                    email:{" "}
                                    <a href={`mailto:${versoMebelContacts.email}`}>
                                        {versoMebelContacts.email}
                                    </a>
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Box>
                                    <Typography
                                        component="p"
                                        sx={{fontSize: "16px", lineHeight: "20px", fontWeight: 500}}
                                    >
                                        +375 44 73 24 144
                                    </Typography>
                                    <Typography component="p" sx={{fontSize: "14px"}}>
                                        Ежедневно, с 8:00 до 18:00
                                    </Typography>
                                </Box>
                                <Typography component="p" sx={{
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                    "& a": {
                                        color: (theme) => theme.palette.primary.main,
                                        textDecoration: "none",
                                    },
                                }}>
                                    email: <a href="mailto:info@upme.by">info@upme.by</a>
                                </Typography>
                            </>
                        )}
                        <Box sx={{display: "flex", alignItems: 'center', gridGap: "36px"}}>
                            <a href="">
                                <ViberIcon/>
                            </a>
                            <a href="">
                                <YoutubeIcon/>
                            </a>
                            <a href="https://telegram.me/upmebel">
                                <TelegramIcon/>
                            </a>
                        </Box>
                    </Box>
                    <Box role="separator" sx={footerDividerSx}/>
                    <Typography sx={footerBottomCopyrightSx}>
                        {isKuhniVersoMebel
                            ? `© 2000-2026 ООО «Алфексгрупп» УНП: ${versoMebelContacts.unp}`
                            : "© 2024 ООО «АЙТИ ДЖУС» УНП: 591397311"}
                    </Typography>
                    <Typography sx={footerBottomPolicySx}>
                        Политика конфиденциальности
                    </Typography>
                    <Box sx={footerBottomCreditsSx}>
                        <Typography sx={footerCreditsLineSx}>
                            Разработка сайта <a href="https://itjuice.by/">«АйтиДжус»</a>
                        </Typography>
                        <Typography sx={footerCreditsLineSx}>
                            Дизайн <a href="https://t.me/HQdesigner">https://t.me/HQdesigner</a>
                        </Typography>
                    </Box>
                </Box>
            </Container>
            <ModalAlert data={alertData} onClose={() => setAlertData((alert) => {
                return {...alert, open: false}
            })}/>
        </Paper>
    );
};

export default Footer;